import {
  ALL_COLUMN_NAMES,
  ColumnValue,
  DEVICES_COLUMN_NAME,
  DevicesColumn,
  DevicesDataItem,
  DevicesQueryPayload,
  IDENTITY_COLUMN_NAME,
  IntegrationInstance,
  MetricManifest,
  RecordBatchInputSchemaItem
} from 'models'
import { getIntegrationLogo } from 'lib/utils'
import { SetFilterValuesFuncParams } from 'ag-grid-enterprise'
import { ColDef, ICellRendererParams } from 'ag-grid-community'
import dayjs from 'dayjs'
import { Box, Typography, Tooltip } from '@mui/material'
import theme from 'theme'
import { ChipTooltip } from 'ui/components/tooltip/ChipTooltip'
import { Dispatch, ReactNode, SetStateAction } from 'react'
import { ActiveFilter, DateFilterValue } from './Devices'
import ActionsCellRenderer from './components/actions-cell-renderer/ActionsCellRenderer'
import SetFilterCellRenderer from './components/SetFilterCellRenderer'

const tooltipStyle = {
  backgroundColor: `${theme.colors.tooltipBg} !important`,
  color: theme.colors.neutral,

  '&::before': {
    backgroundColor: `${theme.colors.tooltipBg} !important`
  },

  '& .MuiTooltip-arrow::before': {
    backgroundColor: `${theme.colors.tooltipBg} !important`
  }
}

/* Shared with the Asset Inventory grid so both records grids tooltip their cells identically. */
export const recordsCellTooltipProps = {
  tooltip: {
    sx: tooltipStyle
  }
}

const tooltipComponentProps = recordsCellTooltipProps

// A gte_/lte_ range column is a date column ONLY when it doesn't also expose `any_`. EMAIL_SIZE_MB
// is a numeric column carrying any_/gte_/lte_ — it must render and query as a set (lookup) filter,
// not a date picker. Real date columns (RECEIVED_AT, etc.) have gte_/lte_/between_ but no any_.
export const isDateRangeColumn = (filterPredicates?: string[] | null): boolean =>
  !!filterPredicates &&
  filterPredicates.length > 1 &&
  filterPredicates.includes('gte_') &&
  filterPredicates.includes('lte_') &&
  !filterPredicates.includes('any_')

// The query API validates a value against the column's manifest type; boolean-looking string columns hold 'true'/'false'.
const toBooleanFilterValue = (raw: unknown, columnType: string): boolean | string => {
  const isTrue = raw === true || raw === 'true'
  return columnType === 'string' ? String(isTrue) : isTrue
}

export const createQueryPayload = (
  obj: Record<string, any>,
  manifestColumns?: RecordBatchInputSchemaItem[],
  dataSourceOptions?: IntegrationInstance[]
): DevicesQueryPayload => {
  if (!obj || !manifestColumns || !manifestColumns.length) return {} as DevicesQueryPayload

  const payload = {} as DevicesQueryPayload

  for (const [columnName, value] of Object.entries(obj)) {
    const columnManifest = manifestColumns.find((col) => col.name === columnName)
    const columnType = columnManifest?.dataType || 'string'
    const filterPredicates = columnManifest?.filterPredicates || []

    if (value && value.filterType === 'boolean') {
      const predicate = filterPredicates.length > 0 ? filterPredicates[0] : 'equal_'
      payload[columnName] = {
        [predicate]: toBooleanFilterValue(value.value, columnType)
      }
      continue
    }

    if (value && value.filterType === 'numberRange') {
      // Numeric threshold carried from the Trends size sections (EMAIL_SIZE_MB). Emit unquoted
      // numbers so Druid compares numerically (EMAIL_SIZE_MB >= 10), per the BE gte_/lte_ schema.
      // Must run before the gte_/lte_ date-range branch below, which assumes timestamp values.
      const out: { gte_?: number; lte_?: number } = {}
      if (typeof value.gte === 'number') out.gte_ = value.gte
      if (typeof value.lte === 'number') out.lte_ = value.lte
      if (Object.keys(out).length) payload[columnName] = out
      continue
    }

    if (
      (columnName === DEVICES_COLUMN_NAME.ONYXIA_PRODUCT_INSTANCE ||
        (columnName as any) === IDENTITY_COLUMN_NAME.ONYXIA_PRODUCT_INSTANCE) &&
      dataSourceOptions
    ) {
      const mappedValues = value.values.map((displayName: string) => {
        const dataSource = dataSourceOptions.find((opt) => opt.name === displayName)
        return dataSource?.instance_name || displayName
      })

      const predicate = filterPredicates.length > 0 ? filterPredicates[0] : 'any_'
      payload[columnName] = {
        [predicate]: mappedValues
      }
      continue
    }

    if (columnType === 'timestamp' || isDateRangeColumn(filterPredicates)) {
      if (value.dateFrom && value.dateTo && value.type === 'inRange') {
        payload[columnName] = {
          between_: [dayjs(value.dateFrom).format('YYYY-MM-DD'), dayjs(value.dateTo).format('YYYY-MM-DD')]
        }
      } else if (value.type === 'before' && value.dateFrom) {
        const predicate = filterPredicates.find((p) => p === 'lte_') || filterPredicates[0] || 'lte_'
        payload[columnName] = {
          [predicate]: dayjs(value.dateFrom).format('YYYY-MM-DD')
        }
      } else if (value.type === 'after' && value.dateFrom) {
        const predicate = filterPredicates.find((p) => p === 'gte_') || filterPredicates[0] || 'gte_'
        payload[columnName] = {
          [predicate]: dayjs(value.dateFrom).format('YYYY-MM-DD')
        }
      }
      continue
    }

    if (filterPredicates.length > 0) {
      const predicate = filterPredicates[0]

      if (columnType === 'string' && filterPredicates.length === 1 && predicate === 'equal_') {
        if (value.values && Array.isArray(value.values)) {
          const booleanValues = value.values.map((val: string) => toBooleanFilterValue(val, columnType))

          if (booleanValues.length === 1) {
            payload[columnName] = {
              [predicate]: booleanValues[0]
            }
          } else {
            payload[columnName] = {
              [predicate]: booleanValues
            }
          }
        } else {
          payload[columnName] = {
            [predicate]: toBooleanFilterValue(value, columnType)
          }
        }
      } else if (value.values && value.values.length === 1 && filterPredicates.length === 1 && predicate === 'any_') {
        payload[columnName] = {
          [predicate]: value.values || value
        }
      } else if (
        value.values &&
        value.values.length === 1 &&
        (value.values[0] === 'true' || value.values[0] === 'false')
      ) {
        payload[columnName] = {
          [predicate]: toBooleanFilterValue(value.values[0], columnType)
        }
      } else {
        payload[columnName] = {
          [predicate]: value.values || value
        }
      }
    } else {
      console.warn(`No filter predicates defined for column: ${columnName}`)
      payload[columnName] = {
        any_: value.values || value
      }
    }
  }

  return payload
}

export const formatCpiName = (name: string) => {
  return `${name.slice(0, 3).toUpperCase()}-${name.slice(-3)}`
}

const displayValue = (value: any): string => {
  if (value === null || value === undefined || value === '') {
    return '—'
  }
  return String(value)
}

interface IGenerateColDefs {
  columns: DevicesColumn[]
  columnValues: ColumnValue[]
  dataSourceOptions: IntegrationInstance[]
  getLookup?: (columnId: string) => Promise<string[]>
  setColumnValues?: Dispatch<SetStateAction<ColumnValue[]>>
  metricManifest?: MetricManifest
}

export const generateColDefs = async ({
  columns,
  columnValues,
  dataSourceOptions,
  getLookup,
  setColumnValues
}: IGenerateColDefs): Promise<ColDef[]> => {
  const res = columns.map((column: DevicesColumn) => {
    if (!column.active) return null

    if (isDateRangeColumn(column.filterPredicates)) {
      return {
        field: column.name,
        headerName: column.displayName,
        headerHeight: 50,
        flex: 2,
        filter: 'agDateColumnFilter',
        sortable: false,
        suppressHeaderMenuButton: true,
        cellRenderer: (params: ICellRendererParams) => {
          return (
            <Tooltip title={displayValue(params.value)} placement="right" arrow componentsProps={tooltipComponentProps}>
              <div
                style={{
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap'
                }}
              >
                {displayValue(params.value)}
              </div>
            </Tooltip>
          )
        },
        filterParams: {
          dateFormat: 'mm/dd/yy',
          resizable: false,
          suppressAndOrCondition: true,
          suppressMiniFilter: true,
          defaultToNothingSelected: true,
          values: async (valueParams: SetFilterValuesFuncParams) => {
            const found = columnValues.find((c) => c.columnName === column.name)
            if (found) {
              await valueParams.success(found.value)
            }
          },
          cellHeight: 38,
          buttons: ['reset'],
          comparator: (filterLocalDateAtMidnight: Date, cellValue: string) => {
            const dateAsString = cellValue
            if (dateAsString == null) return -1
            const dateParts = dateAsString.split('/')
            const cellDate = new Date(Number(dateParts[2]), Number(dateParts[1]) - 1, Number(dateParts[0]))
            if (filterLocalDateAtMidnight.getTime() === cellDate.getTime()) {
              return 0
            }
            if (cellDate < filterLocalDateAtMidnight) {
              return -1
            }
            if (cellDate > filterLocalDateAtMidnight) {
              return 1
            }
          },
          maxNumConditions: 1,
          minValidYear: 2000,
          maxValidYear: 2100,
          inRangeFloatingFilterDateFormat: 'Do MMM YYYY',
          filterOptions: ['before', 'after', 'inRange'],
          debounceMs: 0,
          browserDatePicker: true,
          popupPosition: 'under'
        }
      }
    }

    const isBooleanColumn = column?.filterPredicates?.includes('equal_') && column?.filterPredicates?.length === 1

    if (isBooleanColumn) {
      return {
        field: column.name,
        suppressHeaderMenuButton: true,
        headerName: column.displayName,
        headerHeight: 50,
        sortable: false,
        flex: 2,
        filter: 'booleanFilter',
        filterParams: {
          getLookup,
          columnName: column.name
        },
        cellRenderer: (params: ICellRendererParams) => {
          const value = params.value

          return (
            <Tooltip title={displayValue(params.value)} placement="right" arrow componentsProps={tooltipComponentProps}>
              <div
                style={{
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap'
                }}
              >
                {value === true || value === 'true'
                  ? 'true'
                  : value === false || value === 'false'
                  ? 'false'
                  : displayValue(value)}
              </div>
            </Tooltip>
          )
        }
      }
    }
    return {
      field: column.name,
      suppressHeaderMenuButton: true,
      headerName: column.displayName,
      headerHeight: 50,
      floatingFiltersHeight: 50,
      sortable: false,
      flex: 2,
      filter: 'agSetColumnFilter',
      cellRenderer: (params: ICellRendererParams) => {
        if (params && params.colDef) {
          if (
            params.colDef.field === DEVICES_COLUMN_NAME.ONYXIA_PRODUCT_NAME ||
            params.colDef.field === IDENTITY_COLUMN_NAME.ONYXIA_PRODUCT_NAME
          ) {
            return (
              <div className="source-wrapper">
                <ChipTooltip
                  placement="bottom-start"
                  PopperProps={{
                    sx: {
                      marginTop: `-${theme.spacing.half}px !important`
                    }
                  }}
                  title={
                    <>
                      <Box className="tooltip-wrapper-permissions one-line">
                        <Typography className="tooltip-text">
                          {params.data[DEVICES_COLUMN_NAME.ONYXIA_PRODUCT_NAME] ||
                            params.data[IDENTITY_COLUMN_NAME.ONYXIA_PRODUCT_NAME]}
                        </Typography>
                      </Box>
                    </>
                  }
                >
                  <img
                    src={getIntegrationLogo(
                      params.data[DEVICES_COLUMN_NAME.ONYXIA_PRODUCT_NAME] ||
                        params.data[IDENTITY_COLUMN_NAME.ONYXIA_PRODUCT_NAME]
                    )}
                    width="24px"
                    height="24px"
                    alt=""
                  />
                </ChipTooltip>
              </div>
            )
          }
          if (
            params.colDef.field === DEVICES_COLUMN_NAME.ONYXIA_PRODUCT_INSTANCE ||
            params.colDef.field === IDENTITY_COLUMN_NAME.ONYXIA_PRODUCT_INSTANCE
          ) {
            const value =
              params.data[DEVICES_COLUMN_NAME.ONYXIA_PRODUCT_INSTANCE] ||
              params.data[IDENTITY_COLUMN_NAME.ONYXIA_PRODUCT_INSTANCE]
            const instanceName = dataSourceOptions.find((c) => c.instance_name === value)

            return (
              <Tooltip
                title={displayValue(instanceName?.name)}
                placement="right"
                arrow
                componentsProps={tooltipComponentProps}
              >
                <div
                  style={{
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                  }}
                >
                  {displayValue(instanceName?.name)}
                </div>
              </Tooltip>
            )
          }
          if (
            params.colDef.field === DEVICES_COLUMN_NAME.GROUP_NAMES ||
            params.colDef.field === IDENTITY_COLUMN_NAME.GROUP_NAMES
          ) {
            const value = params.data[DEVICES_COLUMN_NAME.GROUP_NAMES] || params.data[IDENTITY_COLUMN_NAME.GROUP_NAMES]

            if (!value) {
              return <div>—</div>
            }
            let parsedArray

            if (Array.isArray(value)) {
              parsedArray = value
            } else if (typeof value === 'string') {
              try {
                parsedArray = JSON.parse(value)

                if (!Array.isArray(parsedArray)) {
                  parsedArray = [parsedArray]
                }
              } catch (error) {
                parsedArray = [value]
              }
            } else {
              parsedArray = [String(value)]
            }

            const displayText = parsedArray.join(', ')

            return (
              <Tooltip
                title={displayValue(params.value)}
                placement="right"
                arrow
                componentsProps={tooltipComponentProps}
              >
                <div
                  style={{
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                  }}
                >
                  {displayText}
                </div>
              </Tooltip>
            )
          }
        }

        if (typeof params.value === 'string' && params.value.startsWith('[')) {
          try {
            const parsed = JSON.parse(params.value)

            if (Array.isArray(parsed)) {
              const displayText = parsed.join(', ')

              return (
                <Tooltip title={displayText || ''} placement="right" arrow componentsProps={tooltipComponentProps}>
                  <div
                    style={{
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap'
                    }}
                  >
                    {displayText}
                  </div>
                </Tooltip>
              )
            }
          } catch (error) {
            const value = displayValue(params.value)
            return (
              <Tooltip title={value} placement="right" arrow componentsProps={tooltipComponentProps}>
                <div
                  style={{
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                  }}
                >
                  {value}
                </div>
              </Tooltip>
            )
          }
        }

        return (
          <Tooltip title={displayValue(params.value)} placement="right" arrow componentsProps={tooltipComponentProps}>
            <div
              style={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}
            >
              {displayValue(params.value)}
            </div>
          </Tooltip>
        )
      },
      filterParams: {
        includeBlankValues: true,
        values: async (valueParams: SetFilterValuesFuncParams) => {
          const found = columnValues.find((c) => c.columnName === column.name)

          // Helper function to update filter UI for empty state and mini filter visibility
          const handleFilterState = (isEmpty: boolean, valuesCount: number) => {
            setTimeout(() => {
              const filterPopup = document.querySelector('.ag-popup')
              if (!filterPopup) return

              const filterWrapper = filterPopup.querySelector('.ag-filter-wrapper')
              if (!filterWrapper) return

              const setFilterList = filterWrapper.querySelector('.ag-set-filter-list')
              const miniFilter = filterWrapper.querySelector('.ag-mini-filter') as HTMLElement

              // Hide mini filter if 5 or fewer values
              if (miniFilter) {
                miniFilter.style.display = valuesCount <= 5 ? 'none' : ''
              }

              if (isEmpty && setFilterList) {
                // Add empty class and remove has-values class
                filterWrapper.classList.add('filter-empty')
                filterWrapper.classList.remove('filter-has-values')

                // Add "No values for filter" message if not already present
                const existingNoData = filterWrapper.querySelector('.no-data-message')
                if (!existingNoData) {
                  const noDataDiv = document.createElement('div')
                  noDataDiv.className = 'no-data-message'
                  noDataDiv.textContent = 'No values for filter'
                  noDataDiv.style.cssText =
                    'text-align: center; padding: 16px; color: #938F99; font-family: Plus Jakarta Sans, sans-serif; font-size: 14px;'
                  setFilterList.appendChild(noDataDiv)
                }
              } else {
                // Add has-values class and remove empty class
                filterWrapper.classList.add('filter-has-values')
                filterWrapper.classList.remove('filter-empty')

                // Remove "No values for filter" message if present
                const existingNoData = filterWrapper?.querySelector('.no-data-message')
                if (existingNoData) existingNoData.remove()
              }
            }, 50)
          }

          if (
            column.name === DEVICES_COLUMN_NAME.ONYXIA_PRODUCT_INSTANCE ||
            column.name === IDENTITY_COLUMN_NAME.ONYXIA_PRODUCT_INSTANCE
          ) {
            if (found && found.value.length > 0) {
              // We have cached values, use them
              const mappedValues = found.value.map((instanceName) => {
                const dataSource = dataSourceOptions.find((opt) => opt.instance_name === instanceName)
                return dataSource?.name || instanceName
              })
              await valueParams.success(mappedValues)
              handleFilterState(false, mappedValues.length)
              return
            }
            // Not cached: fall through to the getLookup branch below, which fetches the instance
            // names and applies the same instance_name → display name mapping. (Previously this
            // returned an empty list, leaving the column's filter blank unless pre-cached.)
          }

          // We don't have values, fetch them now (lazy loading)
          if (getLookup) {
            try {
              const values = await getLookup(column.name as string)

              if (setColumnValues) {
                setColumnValues((prev) => {
                  // Check if already exists to avoid duplicates
                  const exists = prev.find((cv) => cv.columnName === column.name)
                  if (exists) return prev

                  return [
                    ...prev,
                    {
                      columnName: column.name as ALL_COLUMN_NAMES,
                      value: values
                    }
                  ]
                })
              }

              const isEmpty = !values || values.length === 0
              const valuesCount = values?.length || 0

              if (
                (column as any).name === DEVICES_COLUMN_NAME.ONYXIA_PRODUCT_INSTANCE ||
                (column as any).name === IDENTITY_COLUMN_NAME.ONYXIA_PRODUCT_INSTANCE
              ) {
                const mappedValues = values.map((instanceName) => {
                  const dataSource = dataSourceOptions.find((opt) => opt.instance_name === instanceName)
                  return dataSource?.name || instanceName
                })
                await valueParams.success(mappedValues)
              } else {
                await valueParams.success(values)
              }

              handleFilterState(isEmpty, valuesCount)
            } catch (error) {
              console.error(`Failed to fetch values for column ${column.name}:`, error)
              await valueParams.success([])
              handleFilterState(true, 0)
            }
          } else {
            await valueParams.success([])
            handleFilterState(true, 0)
          }
        },
        resizable: false,
        suppressAndOrCondition: true,
        suppressMiniFilter: false,
        defaultToNothingSelected: true,
        cellHeight: 38,
        cellRenderer: SetFilterCellRenderer,
        cellRendererParams: {
          columnName: column.name,
          dataSourceOptions
        },
        buttons: ['apply', 'reset'],
        closeOnApply: true,
        applyMiniFilterWhileTyping: false, // Don't filter the list while typing
        debounceMs: 500,
        textSearchDebounceMs: 500,
        valueGetter: (params: any) => {
          if (
            column.name === DEVICES_COLUMN_NAME.ONYXIA_PRODUCT_INSTANCE ||
            column.name === IDENTITY_COLUMN_NAME.ONYXIA_PRODUCT_INSTANCE
          ) {
            const dataSource = dataSourceOptions.find((opt) => opt.instance_name === params.value)
            return dataSource?.name || params.value
          }
          return params.value
        }
        // cellRenderer: (params: any) => {
        //   if (
        //     column.name === DEVICES_COLUMN_NAME.ONYXIA_PRODUCT_INSTANCE ||
        //     column.name === IDENTITY_COLUMN_NAME.ONYXIA_PRODUCT_INSTANCE
        //   ) {
        //     const dataSource = dataSourceOptions.find((opt) => opt.instance_name === params.value)
        //
        //     return (
        //       <Tooltip
        //         title={displayValue(params.value)}
        //         placement="right"
        //         arrow
        //         componentsProps={tooltipComponentProps}
        //       >
        //         <div
        //           style={{
        //             overflow: 'hidden',
        //             textOverflow: 'ellipsis',
        //             whiteSpace: 'nowrap'
        //           }}
        //         >
        //           {dataSource?.name || params.value}
        //         </div>
        //       </Tooltip>
        //     )
        //   }
        //
        //   return (
        //     <Tooltip title={displayValue(params.value)} placement="right" arrow componentsProps={tooltipComponentProps}>
        //       <div
        //         style={{
        //           overflow: 'hidden',
        //           textOverflow: 'ellipsis',
        //           whiteSpace: 'nowrap'
        //         }}
        //       >
        //         {params.value}
        //       </div>
        //     </Tooltip>
        //   )
        // }
      }
    }
  })

  return res.filter((c) => !!c) as ColDef[]
}

export const generateSingleColDef = async (
  column: DevicesColumn,
  columnValues: ColumnValue[],
  dataSourceOptions: IntegrationInstance[],
  getLookup?: (columnId: string) => Promise<string[]>,
  setColumnValues?: Dispatch<SetStateAction<ColumnValue[]>>
): Promise<ColDef | null> => {
  // This is essentially the same logic as in generateColDefs but for a single column
  if (isDateRangeColumn(column.filterPredicates)) {
    return {
      field: column.name,
      headerName: column.displayName,
      // eslint-disable-next-line
      // @ts-ignore
      headerHeight: 50,
      flex: 2,
      filter: 'agDateColumnFilter',
      sortable: false,
      suppressHeaderMenuButton: true,
      cellRenderer: (params: ICellRendererParams) => {
        return (
          <Tooltip title={displayValue(params.value)} placement="right" arrow componentsProps={tooltipComponentProps}>
            <div
              style={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}
            >
              {displayValue(params.value)}
            </div>
          </Tooltip>
        )
      },
      filterParams: {
        dateFormat: 'mm/dd/yy',
        resizable: false,
        suppressAndOrCondition: true,
        suppressMiniFilter: true,
        defaultToNothingSelected: true,
        values: async (valueParams: SetFilterValuesFuncParams) => {
          const found = columnValues.find((c) => c.columnName === column.name)
          if (found) {
            await valueParams.success(found.value)
          }
        },
        cellHeight: 38,
        buttons: ['reset'],
        comparator: (filterLocalDateAtMidnight: Date, cellValue: string) => {
          const dateAsString = cellValue
          if (dateAsString == null) return -1
          const dateParts = dateAsString.split('/')
          const cellDate = new Date(Number(dateParts[2]), Number(dateParts[1]) - 1, Number(dateParts[0]))
          if (filterLocalDateAtMidnight.getTime() === cellDate.getTime()) {
            return 0
          }
          if (cellDate < filterLocalDateAtMidnight) {
            return -1
          }
          if (cellDate > filterLocalDateAtMidnight) {
            return 1
          }
        },
        maxNumConditions: 1,
        minValidYear: 2000,
        maxValidYear: 2100,
        inRangeFloatingFilterDateFormat: 'Do MMM YYYY',
        filterOptions: ['before', 'after', 'inRange'],
        debounceMs: 0,
        browserDatePicker: true,
        popupPosition: 'under'
      }
    }
  }

  const isBooleanColumn = column?.filterPredicates?.includes('equal_') && column?.filterPredicates?.length === 1

  if (isBooleanColumn) {
    return {
      field: column.name,
      suppressHeaderMenuButton: true,
      headerName: column.displayName,
      sortable: false,
      flex: 2,
      filter: 'booleanFilter',
      filterParams: {
        getLookup,
        columnName: column.name
      },
      cellRenderer: (params: ICellRendererParams) => {
        const value = params.value

        return (
          <div>
            {value === true || value === 'true' ? 'true' : value === false || value === 'false' ? 'false' : '—'}
          </div>
        )
      }
    }
  }
  return {
    field: column.name,
    suppressHeaderMenuButton: true,
    headerName: column.displayName,
    // eslint-disable-next-line
    // @ts-ignore
    headerHeight: 50,
    floatingFiltersHeight: 50,
    sortable: false,
    flex: 2,
    filter: 'agSetColumnFilter',
    cellRenderer: (params: ICellRendererParams) => {
      if (params && params.colDef) {
        if (
          params.colDef.field === DEVICES_COLUMN_NAME.ONYXIA_PRODUCT_NAME ||
          params.colDef.field === IDENTITY_COLUMN_NAME.ONYXIA_PRODUCT_NAME
        ) {
          return (
            <div className="source-wrapper">
              <ChipTooltip
                placement="bottom-start"
                PopperProps={{
                  sx: {
                    marginTop: `-${theme.spacing.half}px !important`
                  }
                }}
                title={
                  <>
                    <Box className="tooltip-wrapper-permissions one-line">
                      <Typography className="tooltip-text">
                        {params.data[DEVICES_COLUMN_NAME.ONYXIA_PRODUCT_NAME] ||
                          params.data[IDENTITY_COLUMN_NAME.ONYXIA_PRODUCT_NAME]}
                      </Typography>
                    </Box>
                  </>
                }
              >
                <img
                  src={getIntegrationLogo(
                    params.data[DEVICES_COLUMN_NAME.ONYXIA_PRODUCT_NAME] ||
                      params.data[IDENTITY_COLUMN_NAME.ONYXIA_PRODUCT_NAME]
                  )}
                  alt=""
                  width="24px"
                  height="24px"
                />
              </ChipTooltip>
            </div>
          )
        }
        if (
          params.colDef.field === DEVICES_COLUMN_NAME.ONYXIA_PRODUCT_INSTANCE ||
          params.colDef.field === IDENTITY_COLUMN_NAME.ONYXIA_PRODUCT_INSTANCE
        ) {
          const value =
            params.data[DEVICES_COLUMN_NAME.ONYXIA_PRODUCT_INSTANCE] ||
            params.data[IDENTITY_COLUMN_NAME.ONYXIA_PRODUCT_INSTANCE]
          const instanceName = dataSourceOptions.find((c) => c.instance_name === value)

          return (
            <Tooltip
              title={displayValue(instanceName?.name)}
              placement="right"
              arrow
              componentsProps={tooltipComponentProps}
            >
              <div
                style={{
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap'
                }}
              >
                {displayValue(instanceName?.name)}
              </div>
            </Tooltip>
          )
        }
        if (
          params.colDef.field === DEVICES_COLUMN_NAME.GROUP_NAMES ||
          params.colDef.field === IDENTITY_COLUMN_NAME.GROUP_NAMES
        ) {
          const value = params.data[DEVICES_COLUMN_NAME.GROUP_NAMES] || params.data[IDENTITY_COLUMN_NAME.GROUP_NAMES]
          if (!value) {
            return <div>—</div>
          }
          let parsedArray

          if (Array.isArray(value)) {
            parsedArray = value
          } else if (typeof value === 'string') {
            try {
              parsedArray = JSON.parse(value)

              if (!Array.isArray(parsedArray)) {
                parsedArray = [parsedArray]
              }
            } catch (error) {
              parsedArray = [value]
            }
          } else {
            parsedArray = [String(value)]
          }

          const displayText = parsedArray.join(', ')

          return (
            <Tooltip title={displayValue(params.value)} placement="right" arrow componentsProps={tooltipComponentProps}>
              <div
                style={{
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap'
                }}
              >
                {displayText}
              </div>
            </Tooltip>
          )
        }
      }

      if (typeof params.value === 'string' && params.value.startsWith('[')) {
        try {
          const parsed = JSON.parse(params.value)
          if (Array.isArray(parsed)) {
            const displayText = parsed.join(', ')

            return (
              <Tooltip title={displayText} placement="right" arrow componentsProps={tooltipComponentProps}>
                <div
                  style={{
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                  }}
                >
                  {displayText}
                </div>
              </Tooltip>
            )
          }
        } catch (error) {
          return <div>{displayValue(params.value)}</div>
        }
      }

      return (
        <Tooltip title={displayValue(params.value)} placement="right" arrow componentsProps={tooltipComponentProps}>
          <div
            style={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap'
            }}
          >
            {displayValue(params.value)}
          </div>
        </Tooltip>
      )
    },
    filterParams: {
      includeBlankValues: true,
      values: async (valueParams: SetFilterValuesFuncParams) => {
        const found = columnValues.find((c) => c.columnName === column.name)

        // Helper function to update filter UI for empty state and mini filter visibility
        const handleFilterState = (isEmpty: boolean, valuesCount: number) => {
          setTimeout(() => {
            const filterPopup = document.querySelector('.ag-popup')
            if (!filterPopup) return

            const filterWrapper = filterPopup.querySelector('.ag-filter-wrapper')
            if (!filterWrapper) return

            const setFilterList = filterWrapper.querySelector('.ag-set-filter-list')
            const miniFilter = filterWrapper.querySelector('.ag-mini-filter') as HTMLElement

            // Hide mini filter if 5 or fewer values
            if (miniFilter) {
              miniFilter.style.display = valuesCount <= 5 ? 'none' : ''
            }

            if (isEmpty && setFilterList) {
              // Add empty class and remove has-values class
              filterWrapper.classList.add('filter-empty')
              filterWrapper.classList.remove('filter-has-values')

              // Add "No values for filter" message if not already present
              const existingNoData = filterWrapper.querySelector('.no-data-message')
              if (!existingNoData) {
                const noDataDiv = document.createElement('div')
                noDataDiv.className = 'no-data-message'
                noDataDiv.textContent = 'No values for filter'
                noDataDiv.style.cssText =
                  'text-align: center; padding: 16px; color: #938F99; font-family: Plus Jakarta Sans, sans-serif; font-size: 14px;'
                setFilterList.appendChild(noDataDiv)
              }
            } else {
              // Add has-values class and remove empty class
              filterWrapper.classList.add('filter-has-values')
              filterWrapper.classList.remove('filter-empty')

              // Remove "No values for filter" message if present
              const existingNoData = filterWrapper?.querySelector('.no-data-message')
              if (existingNoData) existingNoData.remove()
            }
          }, 50)
        }

        if (
          column.name === DEVICES_COLUMN_NAME.ONYXIA_PRODUCT_INSTANCE ||
          column.name === IDENTITY_COLUMN_NAME.ONYXIA_PRODUCT_INSTANCE
        ) {
          if (found && found.value.length > 0) {
            const mappedValues = found.value.map((instanceName) => {
              const dataSource = dataSourceOptions.find((opt) => opt.instance_name === instanceName)
              return dataSource?.name || instanceName
            })
            await valueParams.success(mappedValues)
            handleFilterState(false, mappedValues.length)
          } else {
            await valueParams.success([])
            handleFilterState(true, 0)
          }
          return
        }

        if (getLookup) {
          try {
            const values = await getLookup(column.name as string)

            if (setColumnValues) {
              setColumnValues((prev) => {
                const exists = prev.find((cv) => cv.columnName === column.name)
                if (exists) return prev

                return [
                  ...prev,
                  {
                    columnName: column.name as ALL_COLUMN_NAMES,
                    value: values
                  }
                ]
              })
            }

            const isEmpty = !values || values.length === 0
            const valuesCount = values?.length || 0
            await valueParams.success(values)
            handleFilterState(isEmpty, valuesCount)
          } catch (error) {
            console.error(`Failed to fetch values for column ${column.name}:`, error)
            await valueParams.success([])
            handleFilterState(true, 0)
          }
        } else {
          await valueParams.success([])
          handleFilterState(true, 0)
        }
      },
      resizable: false,
      suppressAndOrCondition: true,
      suppressMiniFilter: false,
      defaultToNothingSelected: true,
      cellHeight: 38,
      cellRenderer: SetFilterCellRenderer,
      cellRendererParams: {
        columnName: column.name,
        dataSourceOptions
      },
      buttons: ['apply', 'reset'],
      closeOnApply: true,
      applyMiniFilterWhileTyping: false,
      debounceMs: 500,
      textSearchDebounceMs: 500,
      valueGetter: (params: any) => {
        if (
          column.name === DEVICES_COLUMN_NAME.ONYXIA_PRODUCT_INSTANCE ||
          column.name === IDENTITY_COLUMN_NAME.ONYXIA_PRODUCT_INSTANCE
        ) {
          const dataSource = dataSourceOptions.find((opt) => opt.instance_name === params.value)
          return dataSource?.name || params.value
        }
        return params.value
      }
      // cellRenderer: (params: any) => {
      //   if (
      //     column.name === DEVICES_COLUMN_NAME.ONYXIA_PRODUCT_INSTANCE ||
      //     column.name === IDENTITY_COLUMN_NAME.ONYXIA_PRODUCT_INSTANCE
      //   ) {
      //     const dataSource = dataSourceOptions.find((opt) => opt.instance_name === params.value)
      //     return (
      //       <Tooltip title={displayValue(params.value)} placement="right" arrow componentsProps={tooltipComponentProps}>
      //         <div
      //           style={{
      //             overflow: 'hidden',
      //             textOverflow: 'ellipsis',
      //             whiteSpace: 'nowrap'
      //           }}
      //         >
      //           {dataSource?.name || params.value}
      //         </div>
      //       </Tooltip>
      //     )
      //   }
      //   return (
      //     <Tooltip title={displayValue(params.value)} placement="right" arrow componentsProps={tooltipComponentProps}>
      //       <div
      //         style={{
      //           overflow: 'hidden',
      //           textOverflow: 'ellipsis',
      //           whiteSpace: 'nowrap'
      //         }}
      //       >
      //         {params.value}
      //       </div>
      //     </Tooltip>
      //   )
      // }
    }
  }
}

export const insertColDefAtCorrectPosition = (
  currentColDefs: ColDef[],
  newColDef: ColDef,
  allColumns: DevicesColumn[]
): ColDef[] => {
  // Get the order of active columns based on the columns array
  const activeColumns = allColumns.filter((c) => c.active)
  const newColumnName = newColDef.field

  // Find where this new column should be positioned
  const newColumnIndex = activeColumns.findIndex((col) => col.name === newColumnName)

  if (newColumnIndex === -1) {
    // Fallback: add at the end if not found
    return [...currentColDefs, newColDef]
  }

  // Anchor to the grid index of the nearest preceding column rather than counting matches: a
  // preceding column can still occupy a grid slot while hidden, and a count skips those.
  let insertPosition = -1
  for (let i = newColumnIndex - 1; i >= 0 && insertPosition === -1; i--) {
    const existingIndex = currentColDefs.findIndex((colDef) => colDef.field === activeColumns[i].name)
    if (existingIndex !== -1) {
      insertPosition = existingIndex + 1
    }
  }

  if (insertPosition === -1) {
    // Nothing precedes it in the grid yet, so sit ahead of the first schema column rather than
    // ahead of everything — the grid also owns columns that aren't in the manifest schema.
    const firstSchemaIndex = currentColDefs.findIndex((colDef) =>
      allColumns.some((column) => column.name === colDef.field)
    )
    insertPosition = firstSchemaIndex === -1 ? currentColDefs.length : firstSchemaIndex
  }

  const newColDefs = [...currentColDefs]
  newColDefs.splice(insertPosition, 0, newColDef)
  return newColDefs
}

interface IGenerateColumns {
  metricManifest?: MetricManifest
}

export const generateColumns = ({ metricManifest }: IGenerateColumns): DevicesColumn[] => {
  if (!metricManifest) return []

  const recordBatchSpec = metricManifest.druid_spec.record_batch_spec
  if (!recordBatchSpec || !recordBatchSpec.input_schema) return []

  return recordBatchSpec.input_schema
    .map((schemaItem) => {
      return {
        name: schemaItem.name,
        displayName: schemaItem.displayName,
        type: schemaItem.dataType,
        active: schemaItem.displayByDefault,
        filterPredicates: schemaItem.filterPredicates || null
      } as DevicesColumn
    })
    .filter((c) => !!c.filterPredicates)
}

export const getDisplayFilterValue = (
  filter: ActiveFilter,
  dataSourceOptions: IntegrationInstance[]
): string[] | DateFilterValue => {
  if (filter.filterName === 'Instance Name' && Array.isArray(filter.filterValue)) {
    return filter.filterValue.map((instanceName: string) => {
      const found = dataSourceOptions.find((opt) => opt.instance_name === instanceName)
      return found?.name || instanceName
    })
  }
  if ((filter as any).filterValue.type === 'date') {
    return (filter as any).filterValue.dateFrom || (filter as any).filterValue.dateTo
  }
  return filter.filterValue
}

const getTimestampColumns = (metricManifest?: MetricManifest): string[] => {
  if (!metricManifest?.druid_spec?.record_batch_spec?.input_schema) return []

  return metricManifest.druid_spec.record_batch_spec.input_schema
    .filter((column) => column.dataType === 'timestamp' || isDateRangeColumn(column.filterPredicates))
    .map((column) => column.name)
}

export const formatDateValues = (item: DevicesDataItem, metricManifest?: MetricManifest): DevicesDataItem => {
  const formattedItem = { ...item } as any // Cast to any for dynamic property assignment
  const timestampColumns = getTimestampColumns(metricManifest)

  timestampColumns.forEach((columnName) => {
    const value = formattedItem[columnName]
    if (value && typeof value === 'string') {
      formattedItem[columnName] = dayjs(value).format('MM/DD/YYYY HH:mm:ss')
    }
  })

  return formattedItem
}

export const blobToErrorContent = async (blob?: Blob): Promise<{ detail: string } | undefined> => {
  if (!blob) return undefined

  try {
    return JSON.parse(await blob.text()) as { detail: string }
  } catch (e) {
    return undefined
  }
}

export const createActionsColumnDef = (
  dataSourceOptions: IntegrationInstance[],
  onExclude?: (row: DevicesDataItem) => void,
  canExclude = true,
  ticket?: { onCreateTicket: (row: DevicesDataItem) => void; getBlockedReason: () => ReactNode }
): ColDef => {
  return {
    field: 'actions',
    headerName: '',
    width: 140,
    minWidth: 140,
    maxWidth: 140,
    pinned: 'right',
    lockPosition: true,
    suppressMovable: true,
    sortable: false,
    filter: false,
    resizable: false,
    suppressHeaderMenuButton: true,
    cellRenderer: ActionsCellRenderer,
    cellRendererParams: {
      dataSourceOptions,
      onExclude,
      canExclude,
      onCreateTicket: ticket?.onCreateTicket,
      getTicketBlockedReason: ticket?.getBlockedReason
    },
    cellStyle: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: 0
    }
  }
}

export const ROW_ID_KEY = '__rowId'
const FALLBACK_ROW_ID_PREFIX = '__row-'

export const isFallbackRowId = (id: string) => id.startsWith(FALLBACK_ROW_ID_PREFIX)

// AG Grid fails the whole block when getRowId repeats, so a missing or repeated identifier falls back to the row position.
export const withRowIds = <T extends object>(rows: T[], idField: string, startRow: number): T[] => {
  const seen = new Set<string>()
  return rows.map((row, index) => {
    const value = (row as Record<string, unknown>)[idField]
    const id =
      value == null || value === '' || seen.has(String(value))
        ? `${FALLBACK_ROW_ID_PREFIX}${startRow + index}`
        : String(value)
    seen.add(id)
    return { ...row, [ROW_ID_KEY]: id }
  })
}
